/* ============================================================
   TabCompte — onglet « Mon compte » (identité du compte)
   ------------------------------------------------------------
   Regroupe ce qui relève de l'UTILISATEUR, et non du restaurant
   (ce dernier vit dans l'onglet Configuration) :
     - adresse e-mail, avec changement vérifié en deux temps
     - mot de passe
     - identité (prénom, nom, téléphone)
     - langue de l'interface
     - zone RGPD (export / suppression)

   Composant purement présentiel : toute la logique réseau est dans
   TabCompteContainer.
   ============================================================ */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabCompte.module.css';
import MetaHead from '../components/MetaHead';
import DangerZoneRGPD from '../components/DangerZoneRGPD/DangerZoneRGPD';

export default function TabCompte({
  account = null,
  loading = false,
  savingIdentity = false,
  savingEmail = false,
  savingPassword = false,
  savingLang = false,
  feedback = null,          // { type: 'success' | 'error', message }
  onSaveIdentity = () => {},
  onRequestEmailChange = () => {},
  onCancelEmailChange = () => {},
  onChangePassword = () => {},
  onChangeLang = () => {},
}) {
  const { t, i18n } = useTranslation('tabs');

  const [identity, setIdentity] = useState(null);
  const [emailForm, setEmailForm] = useState({ open: false, new_email: '', current_password: '' });
  const [pwdForm, setPwdForm] = useState({ open: false, current: '', next: '', confirm: '' });
  const [pwdError, setPwdError] = useState(null);

  // L'état local d'identité n'est initialisé qu'une fois les données arrivées,
  // pour ne pas figer des champs vides pendant le chargement.
  const ident = identity ?? {
    prenom: account?.prenom || '',
    nom: account?.nom || '',
    telephone: account?.telephone || '',
  };
  const setIdent = (patch) => setIdentity({ ...ident, ...patch });

  const submitPassword = (e) => {
    e.preventDefault();
    setPwdError(null);
    if (pwdForm.next.length < 8) return setPwdError(t('compte.password.too_short'));
    if (pwdForm.next !== pwdForm.confirm) return setPwdError(t('compte.password.mismatch'));
    onChangePassword({ current: pwdForm.current, next: pwdForm.next });
    setPwdForm({ open: false, current: '', next: '', confirm: '' });
  };

  const submitEmail = (e) => {
    e.preventDefault();
    onRequestEmailChange({ new_email: emailForm.new_email, current_password: emailForm.current_password });
    setEmailForm({ open: false, new_email: '', current_password: '' });
  };

  if (loading) {
    return (
      <div className={styles.wrap}>
        <MetaHead page="compte" />
        <div className={styles.loading}>{t('compte.loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <MetaHead page="compte" />

      <header className={styles.tabHeader}>
        <h1>
          <span className={styles.titleEmoji}>👤</span>
          {t('compte.title')}
        </h1>
        <p>{t('compte.subtitle')}</p>
      </header>

      {feedback && (
        <div className={feedback.type === 'error' ? styles.alertError : styles.alertSuccess} role="status">
          {feedback.message}
        </div>
      )}

      {/* ---------- ADRESSE E-MAIL ---------- */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t('compte.email.title')}</h2>
        <p className={styles.cardHint}>{t('compte.email.hint')}</p>

        <div className={styles.readRow}>
          <span className={styles.readLabel}>{t('compte.email.current')}</span>
          <span className={styles.readValue}>{account?.email}</span>
        </div>

        {account?.pending_email && (
          <div className={styles.pendingBox}>
            <div>
              <strong>{t('compte.email.pending_title')}</strong>
              <div className={styles.pendingText}>
                {t('compte.email.pending_text', { email: account.pending_email })}
              </div>
            </div>
            <button type="button" className={styles.btnGhost} onClick={onCancelEmailChange}>
              {t('compte.email.cancel_change')}
            </button>
          </div>
        )}

        {!account?.has_password && (
          <div className={styles.warnBox}>{t('compte.email.no_password')}</div>
        )}

        {account?.has_password && !emailForm.open && (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => setEmailForm({ ...emailForm, open: true })}
          >
            {t('compte.email.change_cta')}
          </button>
        )}

        {emailForm.open && (
          <form className={styles.form} onSubmit={submitEmail}>
            <label className={styles.label}>
              {t('compte.email.new_label')}
              <input
                type="email"
                required
                autoComplete="email"
                className={styles.input}
                value={emailForm.new_email}
                onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                placeholder="nouvelle@adresse.fr"
              />
            </label>
            <label className={styles.label}>
              {t('compte.email.password_label')}
              <input
                type="password"
                required
                autoComplete="current-password"
                className={styles.input}
                value={emailForm.current_password}
                onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
              />
            </label>
            <p className={styles.formNote}>{t('compte.email.form_note')}</p>
            <div className={styles.formActions}>
              <button type="submit" className={styles.btnPrimary} disabled={savingEmail}>
                {savingEmail ? t('compte.saving') : t('compte.email.submit')}
              </button>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => setEmailForm({ open: false, new_email: '', current_password: '' })}
              >
                {t('compte.cancel')}
              </button>
            </div>
          </form>
        )}
      </section>

      {/* ---------- MOT DE PASSE ---------- */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t('compte.password.title')}</h2>
        <p className={styles.cardHint}>
          {account?.has_password ? t('compte.password.hint') : t('compte.password.hint_oauth')}
        </p>

        {account?.has_password && !pwdForm.open && (
          <button type="button" className={styles.btnPrimary} onClick={() => setPwdForm({ ...pwdForm, open: true })}>
            {t('compte.password.change_cta')}
          </button>
        )}

        {pwdForm.open && (
          <form className={styles.form} onSubmit={submitPassword}>
            {pwdError && <div className={styles.alertError}>{pwdError}</div>}
            <label className={styles.label}>
              {t('compte.password.current_label')}
              <input
                type="password" required autoComplete="current-password" className={styles.input}
                value={pwdForm.current}
                onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })}
              />
            </label>
            <label className={styles.label}>
              {t('compte.password.new_label')}
              <input
                type="password" required minLength={8} autoComplete="new-password" className={styles.input}
                value={pwdForm.next}
                onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })}
              />
            </label>
            <label className={styles.label}>
              {t('compte.password.confirm_label')}
              <input
                type="password" required autoComplete="new-password" className={styles.input}
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
              />
            </label>
            <div className={styles.formActions}>
              <button type="submit" className={styles.btnPrimary} disabled={savingPassword}>
                {savingPassword ? t('compte.saving') : t('compte.password.submit')}
              </button>
              <button
                type="button" className={styles.btnGhost}
                onClick={() => { setPwdForm({ open: false, current: '', next: '', confirm: '' }); setPwdError(null); }}
              >
                {t('compte.cancel')}
              </button>
            </div>
          </form>
        )}
      </section>

      {/* ---------- IDENTITÉ ---------- */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t('compte.identity.title')}</h2>
        <p className={styles.cardHint}>{t('compte.identity.hint')}</p>
        <form
          className={styles.form}
          onSubmit={(e) => { e.preventDefault(); onSaveIdentity(ident); }}
        >
          <div className={styles.formGrid}>
            <label className={styles.label}>
              {t('compte.identity.firstname')}
              <input type="text" className={styles.input} value={ident.prenom}
                     onChange={(e) => setIdent({ prenom: e.target.value })} />
            </label>
            <label className={styles.label}>
              {t('compte.identity.lastname')}
              <input type="text" className={styles.input} value={ident.nom}
                     onChange={(e) => setIdent({ nom: e.target.value })} />
            </label>
            <label className={styles.label}>
              {t('compte.identity.phone')}
              <input type="tel" className={styles.input} value={ident.telephone}
                     onChange={(e) => setIdent({ telephone: e.target.value })} />
            </label>
          </div>
          <div className={styles.formActions}>
            <button type="submit" className={styles.btnPrimary} disabled={savingIdentity}>
              {savingIdentity ? t('compte.saving') : t('compte.identity.submit')}
            </button>
          </div>
        </form>
      </section>

      {/* ---------- LANGUE ---------- */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t('compte.lang.title')}</h2>
        <p className={styles.cardHint}>{t('compte.lang.hint')}</p>
        <select
          className={styles.select}
          value={account?.preferred_lang || i18n.language || 'fr'}
          onChange={(e) => onChangeLang(e.target.value)}
          disabled={savingLang}
        >
          <option value="fr">Français</option>
          <option value="en">English</option>
          <option value="es">Español</option>
          <option value="it">Italiano</option>
        </select>
      </section>

      {/* ---------- RGPD ---------- */}
      <DangerZoneRGPD />
    </div>
  );
}
